import type { TutorBillingTableColumn, TutorBillingTableRow } from './interfaces'
import type { TutorBillingChangeRequestAction } from './types'

import { TutorBillingChangeRequestStatus } from '@brighterly/lib-core-types/tutor/enums'

import { useListsStore } from '@shared/model'

import { TutorBillingModal } from './enums'
import { useTutorBillingStore } from './store'
import { useTutorBillingChangeRequestAction } from '../api/change-request.hook'
import { useTutorBillingListApi } from '../api/list.hook'
import { buildTutorBillingChangeComparison } from '../lib/tutor-billing-change-request'
import { formatTutorBillingDate, getTutorBillingTutorName, joinTutorBillingMeta } from '../lib/tutor-billing-fields'

interface ModalTutorBillingChangeRequestReturn {
  isOpen: ComputedRef<boolean>
  isSaving: ComputedRef<boolean>
  hasPendingChangeRequest: ComputedRef<boolean>
  title: ComputedRef<string>
  subtitle: ComputedRef<string | null>
  comment: ComputedRef<string | null>
  columns: TutorBillingTableColumn[]
  rows: ComputedRef<TutorBillingTableRow[]>
  setClose: () => void
  setKeepAsIs: () => Promise<void>
  setConfirm: () => Promise<void>
}

const COLUMNS: TutorBillingTableColumn[] = [
  { key: 'current', label: 'Current' },
  { key: 'new', label: 'New', tone: 'accent' },
]

export const useModalTutorBillingChangeRequest = (): ModalTutorBillingChangeRequestReturn => {
  const store = useTutorBillingStore()
  const listsStore = useListsStore()

  const isOpen = computed<boolean>(() => store.isModal(TutorBillingModal.ChangeRequest))
  const isSaving = computed<boolean>(() => store.isSaving)

  const hasPendingChangeRequest = computed<boolean>(() =>
    store.getActiveRow?.change_request?.status === TutorBillingChangeRequestStatus.Pending)

  const title = computed<string>(() => {
    const row = store.getActiveRow
    const name = row ? getTutorBillingTutorName(row) : ''

    return name ? `Billing info change from ${name}` : 'Billing info change request'
  })

  const subtitle = computed<string | null>(() => {
    const row = store.getActiveRow
    if (!row) return null

    const requestedAt = row.change_request?.requested_at

    return joinTutorBillingMeta([
      `Record ${row.id}`,
      requestedAt ? `requested ${formatTutorBillingDate(requestedAt)}` : null,
    ])
  })

  const comment = computed<string | null>(() =>
    (hasPendingChangeRequest.value && store.getActiveRow?.change_request?.comment) || null)

  const rows = computed<TutorBillingTableRow[]>(() => {
    const row = store.getActiveRow
    if (!row) return []

    return buildTutorBillingChangeComparison(row, listsStore.getTutorBillingPaymentServiceLabel).map(item => ({
      key: item.key,
      label: item.label,
      cells: [
        { value: item.current },
        { value: item.next, tone: item.changed ? 'accent' : 'muted', isChanged: item.changed },
      ],
    }))
  })

  const setClose = (): void => {
    if (store.isSaving) return
    store.closeModal()
  }

  const setAction = async (action: TutorBillingChangeRequestAction): Promise<void> => {
    const row = store.getActiveRow
    if (!row?.change_request || store.isSaving) return

    const updated = await useTutorBillingChangeRequestAction(row.change_request.id, action)
    if (!updated) return

    if (store.isModalFor(TutorBillingModal.ChangeRequest, row.id)) store.closeModal()
    void useTutorBillingListApi()
  }

  const setKeepAsIs = (): Promise<void> => setAction('reject')

  const setConfirm = (): Promise<void> => setAction('accept')

  return {
    isOpen,
    isSaving,
    hasPendingChangeRequest,
    title,
    subtitle,
    comment,
    columns: COLUMNS,
    rows,
    setClose,
    setKeepAsIs,
    setConfirm,
  }
}
